import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import useLayanan from "../../services/queries/useLayanan";
import useUser from "../../services/queries/useUser";
import useVoucher from "../../services/queries/useVoucher";
import toRupiah from "../../utils/toRupiah";

type Props = {
    open: boolean;
    ukmId: number;
    ukmName: string;
    onClose: () => void;
};

export default function UpgradeNaikKelasModal({
    open,
    ukmId,
    ukmName,
    onClose,
}: Props) {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { data: user } = useUser();
    const orderId = React.useMemo(() => moment().format("YYMMDDHHmmss"), []);
    const { data: layanan, isLoading: isLoadingLayanan } = useLayanan({
        isNaikKelas: true,
    });
    const { data: vouchers } = useVoucher({ ukmId });
    const [selectedLayananId, setSelectedLayananId] = React.useState<
        number | ""
    >("");
    const [useSelectedVoucher, setUseSelectedVoucher] = React.useState(false);

    const layananOptions = React.useMemo(
        () => layanan?.filter((item) => item.isHide !== 1) ?? [],
        [layanan],
    );
    const availableVoucher = React.useMemo(
        () => vouchers?.find((voucher) => voucher.voucherAmount > 0),
        [vouchers],
    );
    const selectedLayanan = React.useMemo(
        () => layananOptions.find((item) => item.id === selectedLayananId),
        [layananOptions, selectedLayananId],
    );

    React.useEffect(() => {
        if (selectedLayananId === "" && layananOptions.length > 0) {
            const initialLayanan =
                layananOptions.find((item) => item.initialValue === 1) ??
                layananOptions[0];
            setSelectedLayananId(initialLayanan.id);
        }
    }, [layananOptions, selectedLayananId]);

    React.useEffect(() => {
        if (open) {
            setUseSelectedVoucher(Boolean(availableVoucher));
        }
    }, [availableVoucher, open]);

    const discount = useSelectedVoucher
        ? Math.min(availableVoucher?.nominal ?? 0, selectedLayanan?.harga ?? 0)
        : 0;
    const totalPrice = Math.max((selectedLayanan?.harga ?? 0) - discount, 0);

    const handleLayananChange = (event: SelectChangeEvent<number>) => {
        setSelectedLayananId(Number(event.target.value));
    };

    // const paymentUrl = React.useMemo(() => {
    //     if (!selectedLayanan || !user?.userId) return undefined;

    //     const params = new URLSearchParams();
    //     if (useSelectedVoucher && availableVoucher) {
    //         params.set("voucherId", availableVoucher.id.toString());
    //     }

    //     const query = params.toString();
    //     return `/payment/U-${orderId}${user.userId}/${selectedLayanan.id}/${ukmId}${query ? `?${query}` : ""}`;
    // }, [
    //     availableVoucher,
    //     orderId,
    //     selectedLayanan,
    //     ukmId,
    //     useSelectedVoucher,
    //     user?.userId,
    // ]);

    const paymentUrl = React.useMemo(() => {
        if (!selectedLayanan || !user?.userId) return undefined;

        const params = new URLSearchParams();
        if (useSelectedVoucher && availableVoucher) {
            params.set("voucherId", availableVoucher.id.toString());
        }
        params.set("type", "naik-kelas");

        const query = params.toString();
        return `/payment/U-${orderId}${user.userId}/${selectedLayanan.id}/${ukmId}${query ? `?${query}` : ""}`;
    }, [
        availableVoucher,
        orderId,
        selectedLayanan,
        ukmId,
        useSelectedVoucher,
        user?.userId,
    ]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isPhoneScreen}
            PaperProps={{ sx: { borderRadius: isPhoneScreen ? 0 : 2 } }}
        >
            <DialogTitle sx={{ padding: { xs: 2, sm: 3 } }}>
                <Stack direction="row" justifyContent="space-between" gap={2}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            Perbarui Langganan [{ukmName}]
                        </Typography>
                        <Typography color="text.secondary" marginTop={0.5}>
                            Pilih paket durasi untuk memperbarui langganan UKM.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Tutup"
                        onClick={onClose}
                        sx={{ alignSelf: "flex-start" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ padding: { xs: 2, sm: 3 } }}>
                <Typography fontWeight="bold" marginBottom={2}>
                    Pilih layanan terlebih dahulu:
                </Typography>

                <Typography marginBottom={1}>Pilih layanan:</Typography>
                <FormControl fullWidth>
                    <Select
                        value={selectedLayananId as number}
                        onChange={handleLayananChange}
                        displayEmpty
                        disabled={isLoadingLayanan || layananOptions.length === 0}
                        sx={{ bgcolor: "#F5F5F5" }}
                    >
                        {layananOptions.length === 0 && (
                            <MenuItem value="" disabled>
                                {isLoadingLayanan
                                    ? "Memuat layanan..."
                                    : "Layanan tidak tersedia"}
                            </MenuItem>
                        )}
                        {layananOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.nama}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    marginTop={2}
                >
                    <Box>
                        <Typography>Gunakan Voucher</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {availableVoucher
                                ? `Potongan ${toRupiah(availableVoucher.nominal)} akan digunakan.`
                                : "Voucher tidak tersedia."}
                        </Typography>
                    </Box>
                    <Switch
                        checked={useSelectedVoucher}
                        disabled={!availableVoucher}
                        onChange={(event) =>
                            setUseSelectedVoucher(event.target.checked)
                        }
                        inputProps={{ "aria-label": "Gunakan voucher" }}
                    />
                </Stack>

                <Divider
                    sx={{
                        marginY: 3,
                        borderStyle: "dashed",
                        borderColor: "#9AA3A0",
                    }}
                />

                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                        <Typography>{selectedLayanan?.nama ?? "Layanan"}</Typography>
                        <Typography fontWeight={500}>
                            {toRupiah(selectedLayanan?.harga ?? 0)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                        <Typography>Diskon</Typography>
                        <Typography color="#1BA86B" fontWeight={500}>
                            - {toRupiah(discount)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                        <Typography fontWeight="bold">Total Harga</Typography>
                        <Typography variant="h6" fontWeight="bold">
                            {toRupiah(totalPrice)}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ padding: { xs: 2, sm: 3 }, gap: 1 }}>
                <Button
                    variant="outlined"
                    color="buttonulo"
                    onClick={onClose}
                    sx={{ minWidth: 110, textTransform: "none" }}
                >
                    Kembali
                </Button>
                <Button
                    variant="contained"
                    color="buttonulo"
                    disabled={!paymentUrl}
                    href={paymentUrl}
                    sx={{ minWidth: 110, textTransform: "none" }}
                >
                    Bayar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
